import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getInternalNotes, createInternalNote, updateInternalNote, deleteInternalNote } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, StickyNote, Loader2, Trash2, Edit, Lock, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const NOTE_PREVIEW_LENGTH = 150;

const NoteCard = ({ note, user, isDirection, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = note.content.length > NOTE_PREVIEW_LENGTH;
  const displayContent = expanded || !needsTruncation ? note.content : note.content.slice(0, NOTE_PREVIEW_LENGTH) + '...';

  return (
    <div className="card hover:border-[#2B4257] transition-colors group" data-testid={`note-card-${note.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {note.visibility === 'admin_only' ? (
            <Lock size={14} className="text-[#C25953]" />
          ) : (
            <Globe size={14} className="text-[#4A7C59]" />
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full ${note.visibility === 'admin_only' ? 'bg-[#FBEAE9] text-[#C25953]' : 'bg-[#EDF5F0] text-[#4A7C59]'}`}>
            {note.visibility === 'admin_only' ? 'Solo Admin' : 'Publico'}
          </span>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {(note.author_id === user?.id || isDirection()) && (
            <>
              <Button variant="ghost" size="sm" className="btn-ghost p-1 h-7 w-7" onClick={() => onEdit(note)} data-testid={`btn-edit-note-${note.id}`}>
                <Edit size={14} />
              </Button>
              <Button variant="ghost" size="sm" className="btn-ghost p-1 h-7 w-7 text-[#C25953]" onClick={() => onDelete(note.id)} data-testid={`btn-delete-note-${note.id}`}>
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>
      </div>
      <h3 className="font-semibold text-[#1A2835] mb-2">{note.title}</h3>
      <p className="text-sm text-[#5C7083] whitespace-pre-wrap">{displayContent}</p>
      {needsTruncation && (
        <button onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs font-medium text-[#2B4257] hover:text-[#1A2835] flex items-center gap-1 cursor-pointer"
          data-testid={`btn-expand-note-${note.id}`}>
          {expanded ? <><ChevronUp size={14} /> Ver menos</> : <><ChevronDown size={14} /> Ver mas</>}
        </button>
      )}
      <div className="mt-4 pt-3 border-t border-[#E8E2D7] flex items-center justify-between text-xs text-[#5C7083]">
        <span>{note.author_name}</span>
        <span>{note.created_at?.slice(0, 10)}</span>
      </div>
    </div>
  );
};

const NotasInternas = () => {
  const { user, isDirection } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', visibility: 'public' });

  useEffect(() => { fetchNotes(); }, []);

  const fetchNotes = async () => {
    try {
      const res = await getInternalNotes();
      setNotes(res.data);
    } catch (e) { console.error('Error cargando notas:', e.message); }
    finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingNote) {
        await updateInternalNote(editingNote.id, form);
        toast.success('Nota actualizada');
      } else {
        await createInternalNote(form);
        toast.success('Nota creada');
      }
      setShowDialog(false);
      setEditingNote(null);
      setForm({ title: '', content: '', visibility: 'public' });
      fetchNotes();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta nota?')) return;
    try {
      await deleteInternalNote(id);
      toast.success('Nota eliminada');
      fetchNotes();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setForm({ title: note.title, content: note.content, visibility: note.visibility });
    setShowDialog(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notas-internas-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Notas Internas</h1>
          <p className="page-subtitle">Ideas, mensajes y notas del equipo</p>
        </div>
        <Button className="btn-primary gap-2" onClick={() => { setEditingNote(null); setForm({ title: '', content: '', visibility: 'public' }); setShowDialog(true); }} data-testid="btn-new-note">
          <Plus size={18} /> Nueva Nota
        </Button>
      </div>

      {notes.length === 0 ? (
        <div className="card text-center py-12">
          <StickyNote className="mx-auto h-12 w-12 text-[#E8E2D7] mb-4" />
          <p className="text-[#5C7083]">No hay notas aún. Crea la primera.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notes-grid">
          {notes.map(note => (
            <NoteCard key={note.id} note={note} user={user} isDirection={isDirection} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Note Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingNote ? 'Editar Nota' : 'Nueva Nota'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4" data-testid="note-form">
            <div>
              <Label className="form-label">Título *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="form-input" required data-testid="input-note-title" />
            </div>
            <div>
              <Label className="form-label">Contenido *</Label>
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} className="form-input min-h-[120px] resize-none" required data-testid="input-note-content" />
            </div>
            {isDirection() && (
              <div>
                <Label className="form-label">Visibilidad</Label>
                <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })} className="form-input" data-testid="select-note-visibility">
                  <option value="public">Público (todos ven)</option>
                  <option value="admin_only">Solo Administración</option>
                </select>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="btn-secondary">Cancelar</Button>
              <Button type="submit" className="btn-primary" disabled={saving || !form.title || !form.content} data-testid="btn-save-note">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingNote ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotasInternas;
